import { useLocation, useParams, useSearchParams, history } from '@umijs/max';
import { useEffect, useState, useRef } from 'react';
import useExitMinDetail from './exitMinDetail';
import { isInIframe } from './useDrag';
// 固定参数
const originKeep = [
  'oversea',
  'lang',
  'dragComponentId',
  'sdVersion',
  'log',
  'quanwu',
  'pop',
  'hard',
  'directoryMode',
];

const useRouteParams = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsRef = useRef(searchParams);
  const exitMinDetail = useExitMinDetail();
  const location = useLocation();
  const params = useParams();
  const [currentModule, setCurrentModule] = useState('');
  const [isRoot, setIsRoot] = useState(true);
  const [isQuick, setIsQuick] = useState(false);
  const [isMall, setIsMall] = useState(false);
  const isMallRef = useRef(false);

  /**
   * @description 在3d环境内，每点击一下，记录当前路由
   */
  // const recordRoute = () => {
  //   if (isIn3D) {
  //     setUserSettingInfo('material_home_url', window.location.href)
  //   }
  // }
  /**
   * @description 设置当前模块
   */
  const setCurrentModuleHandler = (pathname: string) => {
    const modules = {
      model: {
        equal: [
          '/',
          '/tool/',
          '/tool',
          '/tool/model/model',
          '/tool/model/model/',
        ],
        includes: ['/tool/model/', '/model/'],
      },
      chartlet: {
        equal: [
          '/chartlet/chartlet/',
          '/chartlet/chartlet',
          '/tool/chartlet/chartlet/',
          '/tool/chartlet/chartlet',
        ],
        includes: ['/tool/chartlet/', '/chartlet/'],
      },
      custom: {
        equal: [
          '/custom/custom/',
          '/custom/custom',
          '/tool/custom/custom/',
          '/tool/custom/custom',
        ],
        includes: ['/tool/custom/', '/custom/'],
      },
    };

    for (const [module, paths] of Object.entries(modules)) {
      if (paths.equal.includes(pathname)) {
        setIsRoot(true);
        setCurrentModule(module);
        return;
      } else if (paths.includes.some((path) => pathname.includes(path))) {
        setIsRoot(false);
        setCurrentModule(module);
        return;
      }
    }
    setIsRoot(false);
    setCurrentModule('');
  };
  /**
   * @description 移除所有查询参数
   */
  const removeAllParams = () => {
    setSearchParams({});
  };
  /**
   * @description 更新查询参数
   */
  const updateSearchParams = ({
    key,
    value = '',
    keepKeys = [],
    removeKeys = [],
    isRemoveAll = false,
    isExitMinDetail = true,
  }: {
    key: string | string[];
    value?: string | string[] | null;
    keepKeys?: string[];
    removeKeys?: string[];
    isRemoveAll?: boolean;
    isKeepAll?: boolean;
    isExitMinDetail?: boolean;
  }) => {
    if (isExitMinDetail) {
      exitMinDetail();
    }
    const newSearchParams = new URLSearchParams(searchParamsRef.current);
    // 确保 key 和 value 是数组
    const keys = Array.isArray(key) ? key : [key];
    const values = Array.isArray(value) ? value : [value];

    if (isRemoveAll) {
      // 仅保留指定的 key
      const currentValues = keys
        .map((k) => newSearchParams.get(k))
        .filter((v) => v !== null);

      // 清空所有参数
      newSearchParams.forEach((_, paramKey) => {
        newSearchParams.delete(paramKey);
      });

      // 仅设置指定的 key
      currentValues.forEach((currentValue, index) => {
        if (currentValue !== null) {
          newSearchParams.set(keys[index], currentValue);
        }
      });
    } else {
      // 设置或更新参数
      keys.forEach((k, index) => {
        const val = values[index];
        if (val) {
          newSearchParams.set(k, val);
        } else {
          newSearchParams.delete(k); // 如果 value 为空，删除该参数
        }
      });

      // 移除指定的参数
      removeKeys.forEach((removeKey) => {
        newSearchParams.delete(removeKey);
      });

      // 保留不变的参数
      originKeep.concat(keepKeys).forEach((keepKey) => {
        // const keepValue = searchParams.get(keepKey);
        // if (keepValue) {
        //   newSearchParams.set(keepKey, keepValue);
        // }
        if (searchParamsRef.current.has(keepKey)) {
          const value = searchParamsRef.current.get(keepKey) || '';
          // 直接添加参数，即使值为空
          newSearchParams.set(keepKey, value);
        }
      });
    }
    setSearchParams(newSearchParams);
    searchParamsRef.current = newSearchParams;
  };

  const jumpRoute = (
    path: string,
    keepKeys: string[] = [],
    updateParams?: Record<string, string>,
  ) => {
    exitMinDetail();
    // 内置默认参数
    const defaultParams = { searchImg: '', keyword: '' };
    // 合并参数，外部传入的参数优先级更高
    const mergedParams = { ...defaultParams, ...updateParams };
    // 创建一个新的 URLSearchParams 实例
    const newSearchParams = new URLSearchParams();
    // 保留指定的参数
    originKeep.concat(keepKeys).forEach((keepKey) => {
      if (searchParams.has(keepKey)) {
        const value = searchParams.get(keepKey) || '';
        // 直接添加参数，即使值为空
        newSearchParams.set(keepKey, value);
      }
    });
    // 更新或设置新的参数
    Object.entries(mergedParams).forEach(([key, value]) => {
      if (value) {
        newSearchParams.set(key, value);
      } else {
        newSearchParams.delete(key); // 如果 value 为空，删除该参数
      }
    });
    // 构建新的 URL
    const newUrl = `${path}?${newSearchParams.toString()}`;
    // 路由前缀
    const prefix = location?.pathname.includes('/tool') ? '/tool' : '';
    // 跳转到新的路由
    history.push(prefix + newUrl);
  };

  useEffect(() => {
    setCurrentModuleHandler(location?.pathname);
    setIsQuick(
      [
        '/circle',
        '/my-model',
        '/favorites',
        '/recently',
        '/brand',
        '/latest-model',
      ].some((path) => location?.pathname.includes(path)),
    );
    const isMallValue = location?.pathname.includes('/mall');
    setIsMall(isMallValue);
    isMallRef.current = isMallValue;
  }, [location]);

  /**
   * @description 同步searchParamsRef和searchParams，确保ref始终反映最新的URL参数状态
   * 当URL参数通过其他方式改变（浏览器前进/后退、直接修改URL等）时，及时更新ref
   */
  useEffect(() => {
    searchParamsRef.current = searchParams;
  }, [searchParams]);

  const defaultLibrarySource = isInIframe ? '1' : '0';
  const librarySourceParam = searchParams.get('librarySource');

  const obj = {
    params,
    currentModule,
    categoryId: params?.categoryId || '',
    brandCategoryId: searchParams.get('brandCategoryId') || '',
    brandId: searchParams.get('brandId') || '',
    brandName: searchParams.get('brandName') || '',
    tool: location?.pathname.includes('/tool'),
    detailId: params?.detailId || '',
    is3DMaterial: searchParams.get('is3DMaterial') || '',
    submenu: Number(params?.['*']) === 0 ? undefined : Number(params?.['*']),
    sort: searchParams.get('sort') || 0,
    librarySource: librarySourceParam || defaultLibrarySource,
    keyword: searchParams.get('keyword'),
    // 搜索范围
    scope: searchParams.get('scope'),
    // 搜索默认的searchLibrarySource
    searchLibrarySource:
      searchParams.get('searchLibrarySource') ||
      (isInIframe ? '1' : librarySourceParam),
    latest: searchParams.get('latest') || '',
    searchImg: searchParams.get('searchImg') || '',
    originImg: searchParams.get('originImg') || '',
    tagIds: searchParams.get('tagIds') || '',
    searchCategoryId: searchParams.get('searchCategoryId') || '',
    tab: searchParams.get('tab') || 'active',
    mallCategoryId: params?.mallCategoryId || '',
    subMallCategoryId: params?.subMallCategoryId || '',
    subSubMallCategoryId: params?.subSubMallCategoryId || '',
    isRoot,
    isQuick,
    isMall,
    isMallRef,
    location,
    removeAllParams,
    updateSearchParams,
    jumpRoute,
  };
  return {
    ...obj,
  };
};

export default useRouteParams;
